import { PatientNotificationQueuesDto } from "common-util-experience-client";
import { AppointmentConstants } from "../../../constants/Appointment";
import { formatDateTimeToLocalNoTz } from "../dateFormat";

interface AppointmentNotificationsCardProps {
  /** Null while loading. */
  notifications: PatientNotificationQueuesDto[] | null;
  /** Reminders can only be resent while the appointment is still ahead or under way. */
  canResend: boolean;
  isEmailResendEnabled: boolean;
  isSMSResendEnabled: boolean;
  onResend: (reminderType: string) => void;
}

const notificationTime = (notification: PatientNotificationQueuesDto): string => {
  if (notification.status === AppointmentConstants.REMINDER_STATUS_FAILED) {
    return "Delivery failed";
  }
  if (notification.deliveredAt) {
    return `Sent ${formatDateTimeToLocalNoTz(notification.deliveredAt as unknown as string)}`;
  }
  if (notification.scheduledAt) {
    return `Scheduled ${formatDateTimeToLocalNoTz(notification.scheduledAt as unknown as string)}`;
  }
  return "Not scheduled";
};

export const AppointmentNotificationsCard = ({
  notifications,
  canResend,
  isEmailResendEnabled,
  isSMSResendEnabled,
  onResend,
}: AppointmentNotificationsCardProps) => {
  const grouped = new Map<string, PatientNotificationQueuesDto[]>();
  notifications?.forEach((notification) => {
    const group = grouped.get(notification.referenceType) ?? [];
    group.push(notification);
    grouped.set(notification.referenceType, group);
  });

  return (
    <section className="appointment-card appointment-notifications">
      <div className="appointment-card__header">
        <h3 className="appointment-card__title">Patient Messages</h3>
      </div>
      {/* Only the list scrolls when the card's height is capped (wide screens). */}
      <div className="appointment-notifications__list">
        {notifications === null && (
          <div className="placeholder-wave" data-testid="notifications-loading">
            <span className="placeholder appointment-notification__placeholder"></span>
            <span className="placeholder appointment-notification__placeholder"></span>
          </div>
        )}
        {notifications?.length === 0 && (
          <span className="appointment-card__empty">No messages sent yet</span>
        )}
        {Array.from(grouped.entries()).map(([referenceType, rows]) => (
          <div key={referenceType} className="appointment-notifications__group">
            <div className="appointment-card__eyebrow">{referenceType}</div>
            {rows.map((notification, index) => {
              const isSms = notification.remainderType === AppointmentConstants.SMS;
              const isFailed =
                notification.status === AppointmentConstants.REMINDER_STATUS_FAILED;
              const isReminder =
                notification.referenceType ===
                AppointmentConstants.APPOINTMENT_REMINDER_NOTIFICATION;
              const isResendEnabled = isSms ? isSMSResendEnabled : isEmailResendEnabled;

              return (
                <div
                  key={notification.id ?? index}
                  className={`appointment-notification${isFailed ? " appointment-notification--failed" : ""}`}
                >
                  <span className="appointment-notification__icon material-symbols-outlined">
                    {isSms ? "chat_bubble" : "mail"}
                  </span>
                  <div className="appointment-notification__text">
                    <div className="text-truncate">
                      <strong>{isSms ? "SMS" : "Email"}</strong>
                      {notification.toAddress && (
                        <span className="text-muted"> · {notification.toAddress}</span>
                      )}
                    </div>
                    <div className="appointment-notification__time">
                      {notificationTime(notification)}
                    </div>
                  </div>
                  {isReminder && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary appointment-notification__resend"
                      disabled={!canResend || !isResendEnabled}
                      onClick={() => onResend(notification.remainderType)}
                    >
                      Resend
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </section>
  );
};
